/** Interactive Comps tool — v1 js/comps.js, port with the same contract:
 *
 *   - per-lead state lives at LEAD_EDITS[addr].compsTool = {filters, saved}
 *     (v1 also persists results/subject there; v2 keeps those in IndexedDB —
 *     they're a refetchable search cache, and the org blob stays lean).
 *     saved is keyed by lowercased comp address, each entry {weight, ...comp} —
 *     exactly what v1's rail, map, and summary read.
 *   - search calls the SAME backend endpoint v1 calls: GET /api/arv/comps
 *     with v1's exact query params; photos lazy-load via /api/arv/photos.
 *   - projected ARV = weighted avg $/sqft of SAVED comps (or ALL results
 *     until something is starred) × subject sqft; "save to lead" writes
 *     edits.arv as "$X,XXX" (v1 cmpApplyArv).
 *
 * Persistence uses the Phase 12 rep (replace-keys) patch so unstarring a
 * comp actually deletes it — a deep merge would resurrect it.
 */
import { backendFetch, type BackendOptions } from "../../lib/backend";
import { natKey } from "../../lib/natkey";
import { fmtMoneyEdit } from "../edits/api";
import type { RecOp } from "../../lib/outbox";
import type { Lead } from "../../types";

// ---- state shapes ------------------------------------------------------------

/** v1 filter keys, verbatim (comps.js cmpState). Strings mirror the inputs;
 * srcZ/srcR/srcH default ON via `!== false`, exactly like v1's pills. */
export interface CompsFilters {
  radius: string | number;
  days: string | number;
  beds: string | number;
  baths: string | number;
  sqftMin: string | number;
  sqftMax: string | number;
  yearMin: string | number;
  yearMax: string | number;
  priceMin?: string;
  priceMax?: string;
  sold: boolean;
  active: boolean;
  pending: boolean;
  srcZ?: boolean;
  srcR?: boolean;
  srcH?: boolean;
  /** v1: prefill-from-subject has run once for this lead */
  _auto?: number;
}

export interface CompRow {
  address: string;
  price: number;
  ppsf: number;
  sqft: number;
  beds: number | null;
  baths: number | null;
  year: number | null;
  sold_date: string | null;
  cdom: number | null;
  dist_mi: number | null;
  status: string;
  source: string;
  lat: number | null;
  lng: number | null;
  photo: string | null;
  photos?: string[];
  link: string | null;
}

export interface SavedComp extends CompRow {
  weight: number;
}

export interface CompsSubject {
  address?: string;
  lat?: number;
  lng?: number;
}

export interface CompsToolState {
  filters: CompsFilters;
  saved: Record<string, SavedComp>;
  results: CompRow[];
  subject: CompsSubject | null;
}

export const defaultCompFilters = (): CompsFilters => ({
  radius: 0.35,
  days: 180,
  beds: "",
  baths: "",
  sqftMin: "",
  sqftMax: "",
  yearMin: "",
  yearMax: "",
  sold: true,
  active: false,
  pending: false,
});

const numOf = (v: unknown): number | null => {
  const f = parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return f > 0 ? f : null;
};

/** v1's one-time prefill from the subject: sqft ±400, year built ±20, same
 * beds, floor(baths). Mutates nothing — returns the filled copy. */
export function autoPrefillFilters(f: CompsFilters, lead: Pick<Lead, "sqft" | "yearBuilt" | "beds" | "baths">, edits?: Record<string, unknown>): CompsFilters {
  if (f._auto) return f;
  const out = { ...f, _auto: 1 };
  const sq = lead.sqft ?? numOf(edits?.["sqft"]);
  const yb = numOf(edits?.["yearBuilt"]) ?? lead.yearBuilt;
  if (!out.sqftMin && sq) {
    out.sqftMin = Math.max(0, Math.round(sq - 400));
    out.sqftMax = Math.round(sq + 400);
  }
  if (!out.yearMin && yb) {
    out.yearMin = Math.round(yb - 20);
    out.yearMax = Math.round(yb + 20);
  }
  if (!out.beds && lead.beds) out.beds = Math.round(lead.beds);
  if (!out.baths && lead.baths) out.baths = Math.floor(lead.baths);
  return out;
}

// ---- reading persisted state -------------------------------------------------

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** Tolerant read of fields.compsTool — written by v1 (full state incl.
 * results) or v2 ({filters, saved}). Junk in → clean default out. */
export function narrowCompsTool(raw: unknown): CompsToolState {
  const empty: CompsToolState = { filters: defaultCompFilters(), saved: {}, results: [], subject: null };
  if (!isObj(raw)) return empty;
  const filters = isObj(raw["filters"]) ? { ...defaultCompFilters(), ...(raw["filters"] as Partial<CompsFilters>) } : defaultCompFilters();
  const saved: Record<string, SavedComp> = {};
  if (isObj(raw["saved"])) {
    for (const [k, v] of Object.entries(raw["saved"])) {
      if (!isObj(v)) continue; // drop stray nulls — v1 crashes on them, we don't write them
      saved[k] = { weight: 1, ...(narrowComp(v) ?? {}), ...(typeof v["weight"] === "number" ? { weight: v["weight"] } : {}) } as SavedComp;
      if (!saved[k].address) delete saved[k];
    }
  }
  const results = Array.isArray(raw["results"]) ? (raw["results"] as unknown[]).map(narrowComp).filter((c): c is CompRow => c !== null) : [];
  const subject = isObj(raw["subject"]) ? (raw["subject"] as CompsSubject) : null;
  return { filters, saved, results, subject };
}

export function narrowComp(raw: unknown): CompRow | null {
  if (!isObj(raw)) return null;
  const addr = typeof raw["address"] === "string" ? raw["address"] : "";
  if (!addr) return null;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  return {
    address: addr,
    price: num(raw["price"]) ?? 0,
    ppsf: num(raw["ppsf"]) ?? 0,
    sqft: num(raw["sqft"]) ?? 0,
    beds: num(raw["beds"]),
    baths: num(raw["baths"]),
    year: num(raw["year"]),
    sold_date: str(raw["sold_date"]),
    cdom: num(raw["cdom"]),
    dist_mi: num(raw["dist_mi"]),
    status: str(raw["status"]) ?? "sold",
    source: str(raw["source"]) ?? "zillow",
    lat: num(raw["lat"]),
    lng: num(raw["lng"]),
    photo: str(raw["photo"]),
    ...(Array.isArray(raw["photos"]) ? { photos: (raw["photos"] as unknown[]).filter((p): p is string => typeof p === "string") } : {}),
    link: str(raw["link"]),
  };
}

/** v1 keys saved comps by lowercased address. */
export const compKey = (c: Pick<CompRow, "address">): string => (c.address || "").toLowerCase();

// ---- the search --------------------------------------------------------------

/** v1 cmpSearch's exact query string for GET /api/arv/comps. */
export function compsQuery(address: string, f: CompsFilters): string {
  const stats = (["sold", "active", "pending"] as const).filter((k) => f[k]);
  const srcs = ([["srcZ", "zillow"], ["srcR", "realtor"], ["srcH", "homes"]] as const)
    .filter(([k]) => f[k] !== false)
    .map(([, v]) => v);
  const q = new URLSearchParams({
    address: address || "",
    radius: String(f.radius || 0.35),
    days_back: String(f.days || 180),
    beds: String(f.beds || 0),
    baths: String(f.baths || 0),
    sqft_min: String(f.sqftMin || 0),
    sqft_max: String(f.sqftMax || 0),
    year_min: String(f.yearMin || 0),
    year_max: String(f.yearMax || 0),
    statuses: stats.join(","),
    price_min: String((f.priceMin ?? "").replace(/[^0-9.]/g, "") || 0),
    price_max: String((f.priceMax ?? "").replace(/[^0-9.]/g, "") || 0),
    sources: srcs.join(",") || "zillow",
  });
  return q.toString();
}

export async function searchComps(
  token: string | null,
  address: string,
  f: CompsFilters,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<{ comps: CompRow[]; subject: CompsSubject | null }> {
  const j = await backendFetch<{ comps?: unknown; subject?: unknown }>(
    token,
    "/api/arv/comps?" + compsQuery(address, f),
    { ...opts },
  );
  const comps = Array.isArray(j?.comps) ? (j.comps as unknown[]).map(narrowComp).filter((c): c is CompRow => c !== null) : [];
  const subject = isObj(j?.subject) ? (j.subject as CompsSubject) : null;
  return { comps, subject };
}

/** v1 cmpLoadPhotos: fetch the full photo set for a comp (lazy, once). */
export async function fetchCompPhotos(
  token: string | null,
  comp: Pick<CompRow, "link" | "address">,
  opts?: Pick<BackendOptions, "signal">,
): Promise<string[]> {
  const q = new URLSearchParams({ url: comp.link || "", address: comp.address || "" });
  const j = await backendFetch<{ photos?: unknown }>(token, "/api/arv/photos?" + q.toString(), { ...opts });
  return Array.isArray(j?.photos) ? (j.photos as unknown[]).filter((p): p is string => typeof p === "string") : [];
}

// ---- summary math (v1 cmpSummaryHTML, verbatim) ------------------------------

export interface CompsSummary {
  count: number;
  /** nothing starred yet — summarizing ALL results ("star to refine") */
  usingAll: boolean;
  avgPpsf: number | null;
  avgCdom: number | null;
  arv: number | null;
}

export function compsSummary(state: Pick<CompsToolState, "saved" | "results">, subjectSqft: number | null): CompsSummary {
  let saved: { weight?: number; ppsf: number; cdom: number | null }[] = Object.values(state.saved ?? {});
  const usingAll = saved.length === 0 && (state.results ?? []).length > 0;
  if (usingAll) saved = state.results.map((c) => ({ weight: 1, ppsf: c.ppsf, cdom: c.cdom }));
  if (saved.length === 0) return { count: 0, usingAll: false, avgPpsf: null, avgCdom: null, arv: null };
  let wsum = 0, psum = 0, csum = 0, cn = 0;
  for (const c of saved) {
    const w = c.weight || 1;
    wsum += w;
    psum += (c.ppsf || 0) * w;
    if (c.cdom !== null && c.cdom !== undefined) {
      csum += c.cdom;
      cn++;
    }
  }
  const avgPpsf = psum / Math.max(wsum, 0.001);
  const avgCdom = cn ? Math.round(csum / cn) : null;
  const arv = subjectSqft ? Math.round(avgPpsf * subjectSqft) : null;
  return { count: saved.length, usingAll, avgPpsf, avgCdom, arv };
}

// ---- persistence ops ---------------------------------------------------------

/** What goes to the org (the door): the user's actual input — filters +
 * starred comps. results/subject stay in IndexedDB (refetchable cache).
 * rep:['compsTool'] = replace, so unstars really delete (Phase 12). */
export function opPersistCompsTool(address: string, state: Pick<CompsToolState, "filters" | "saved">): RecOp {
  return {
    s: "lead_fields",
    op: "patch",
    k: natKey(address),
    d: { compsTool: { filters: state.filters, saved: state.saved } },
    rep: ["compsTool"],
  };
}

/** v1 cmpApplyArv: edits.arv = "$X,XXX". (comped/listed overrides still win
 * in both apps' display chains — same as v1.) */
export function opApplyCompsArv(address: string, arv: number): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { arv: fmtMoneyEdit(arv) } };
}

/** v1's toast strings, pinned (the UI shows them verbatim). */
export const COMPS_MSG = {
  needStatus: "Pick at least one status",
  searching: "Searching comps…",
  found: (n: number) => `${n} comps found` + (n && n < 3 ? " — thin area: try a bigger radius or more days back" : ""),
  unreachable: "Could not reach the backend",
  waking: "Backend waking up (free server) — retrying in 10s…",
  arvSaved: "ARV saved to the lead ✓",
};
